import { useState } from "react";
import {
  RiSendPlaneLine,
  RiUserAddLine,
  RiFileUploadLine,
  RiMailDownloadLine,
  RiRefreshLine,
  RiMailSettingsLine,
  RiDatabaseLine,
  RiTeamLine,
  RiListSettingsLine,
  RiMailCheckLine,
  RiFileExcel2Line,
  RiArchiveLine,
} from "react-icons/ri";
import LoadingSpinner from "../components/common/LoadingSpinner";

const Actions = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(false);

  const categories = [
    { id: "all", name: "All Actions" },
    { id: "messaging", name: "Messaging" },
    { id: "contacts", name: "Contacts" },
    { id: "data", name: "Data Management" },
    { id: "system", name: "System" },
  ];

  const actions = [
    {
      id: "quick-send",
      name: "Quick Send",
      description: "Send a message to recent recipients",
      icon: RiSendPlaneLine,
      color: "from-blue-500 to-blue-600",
      category: "messaging",
      action: () => handleAction("Sending message..."),
    },
    {
      id: "import-contacts",
      name: "Import Contacts",
      description: "Import contacts from CSV or Excel",
      icon: RiFileUploadLine,
      color: "from-green-500 to-green-600",
      category: "contacts",
      action: () => handleAction("Importing contacts..."),
    },
    {
      id: "export-reports",
      name: "Export Reports",
      description: "Download analytics reports",
      icon: RiMailDownloadLine,
      color: "from-purple-500 to-purple-600",
      category: "data",
      action: () => handleAction("Generating reports..."),
    },
    {
      id: "sync-data",
      name: "Sync Data",
      description: "Sync with external services",
      icon: RiRefreshLine,
      color: "from-orange-500 to-orange-600",
      category: "system",
      action: () => handleAction("Syncing data..."),
    },
    {
      id: "email-validation",
      name: "Validate Emails",
      description: "Check email list for validity",
      icon: RiMailCheckLine,
      color: "from-teal-500 to-teal-600",
      category: "contacts",
      action: () => handleAction("Validating emails..."),
    },
    {
      id: "template-management",
      name: "Manage Templates",
      description: "Organize and update message templates",
      icon: RiMailSettingsLine,
      color: "from-pink-500 to-pink-600",
      category: "messaging",
      action: () => handleAction("Loading templates..."),
    },
    {
      id: "backup-data",
      name: "Backup Data",
      description: "Create system backup",
      icon: RiDatabaseLine,
      color: "from-indigo-500 to-indigo-600",
      category: "system",
      action: () => handleAction("Creating backup..."),
    },
    {
      id: "export-contacts",
      name: "Export Contacts",
      description: "Download contact list as CSV",
      icon: RiFileExcel2Line,
      color: "from-emerald-500 to-emerald-600",
      category: "contacts",
      action: () => handleAction("Exporting contacts..."),
    },
    {
      id: "archive-messages",
      name: "Archive Messages",
      description: "Archive old messages and campaigns",
      icon: RiArchiveLine,
      color: "from-amber-500 to-amber-600",
      category: "data",
      action: () => handleAction("Archiving messages..."),
    },
    {
      id: "manage-lists",
      name: "Manage Lists",
      description: "Organize contact lists and segments",
      icon: RiListSettingsLine,
      color: "from-cyan-500 to-cyan-600",
      category: "contacts",
      action: () => handleAction("Loading lists..."),
    },
  ];

  const handleAction = async (loadingMessage) => {
    setIsLoading(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsLoading(false);
  };

  const filteredActions =
    activeCategory === "all"
      ? actions
      : actions.filter((action) => action.category === activeCategory);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-secondary-900">Quick Actions</h1>
        {isLoading && (
          <div className="flex items-center space-x-2 text-primary-600">
            <LoadingSpinner />
            <span>Processing...</span>
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="flex space-x-2 overflow-x-auto pb-2">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
              activeCategory === category.id
                ? "bg-primary-500 text-white"
                : "bg-white text-secondary-600 hover:bg-secondary-50"
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredActions.map((action) => (
          <button
            key={action.id}
            onClick={action.action}
            disabled={isLoading}
            className="p-6 rounded-xl bg-white border border-secondary-200 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-left"
          >
            <div
              className={`w-14 h-14 rounded-xl bg-gradient-to-r ${action.color} flex items-center justify-center mb-4`}
            >
              <action.icon className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-secondary-900">
              {action.name}
            </h3>
            <p className="text-secondary-600 mt-1">{action.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Actions;
